import { useEffect, useState } from 'react';
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  Search,
  Shield,
  Trash2,
  UserCheck,
  UserX,
  Users,
} from 'lucide-react';
import { adminApi, countriesApi, usersApi } from '../../../api';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { ROLE_KEYS, getSessionRole } from '../../../utils/roleUtils';
import { resolveUserAvatar } from '../../../utils/media';
import { useUIStore } from '../../../store';

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color = 'primary.main' }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: alpha(color, 0.04),
      flex: 1,
      minWidth: 140,
    }}
  >
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: alpha(color, 0.1),
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={22} />
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const normalizeRole = (value) => {
  if (!value) return '';
  const raw = String(value).trim().toLowerCase();
  if (!raw) return '';
  const condensed = raw.replace(/[^a-z0-9]/g, '');
  if (condensed === 'superadmin') return ROLE_KEYS.SUPER_ADMIN;
  if (condensed === 'admin') return ROLE_KEYS.ADMIN;
  if (condensed === 'agentadmin') return ROLE_KEYS.AGENT_ADMIN;
  if (condensed === 'agent') return ROLE_KEYS.AGENT;
  if (condensed === 'client') return ROLE_KEYS.CLIENT;
  return raw;
};

const UsersPage = ({
  data = [],
  sess = null,
  session = null,
  clist = [],
}) => {
  const [users, setUsers] = useState(data);
  const [countries, setCountries] = useState(clist);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedImage, setSelectedImage] = useState('');
  const rowsPerPage = 10;

  const [username, setUsername] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [country, setCountry] = useState('82');
  const [userRole, setUserRole] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userAvatarFile, setUserAvatarFile] = useState(null);
  const [userAvatarPreview, setUserAvatarPreview] = useState('');
  const [addErrors, setAddErrors] = useState({});

  const [editId, setEditId] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editMobile, setEditMobile] = useState('');
  const [editCountry, setEditCountry] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editAvatarFile, setEditAvatarFile] = useState(null);
  const [editAvatarPreview, setEditAvatarPreview] = useState('');
  const [editErrors, setEditErrors] = useState({});

  const showSnackbar = useUIStore((state) => state.showSnackbar);
  const effectiveSession = sess && Object.keys(sess).length ? sess : session;
  const role = getSessionRole(effectiveSession);
  const canAddUser = [ROLE_KEYS.CLIENT, ROLE_KEYS.ADMIN, ROLE_KEYS.SUPER_ADMIN].includes(role);

  useEffect(() => {
    fetchCountries();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!successMsg) return undefined;
    showSnackbar({ message: successMsg, severity: 'success' });
    const timer = setTimeout(() => setSuccessMsg(''), 3000);
    return () => clearTimeout(timer);
  }, [showSnackbar, successMsg]);

  const fetchCountries = async () => {
    try {
      const response = await countriesApi.getCountryCodes();
      const result = response?.data?.data || response?.data || [];
      setCountries(Array.isArray(result) ? result : []);
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to load countries');
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const response = await adminApi.getUsers();
      const result = response?.data?.data || response?.data || [];
      setUsers(Array.isArray(result) ? result : []);
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    try {
      const response = await adminApi.updateUser(id, { status: newStatus });
      const updated = response?.data?.data || response?.data || null;
      if (updated) {
        setUsers((prev) => prev.map((user) => (user.uid === id ? { ...user, ...updated } : user)));
      } else {
        setUsers((prev) =>
          prev.map((user) => (user.uid === id ? { ...user, u_status: newStatus } : user))
        );
      }
      setSuccessMsg('User status updated successfully');
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to update status');
    }
  };

  const handleDeleteConfirm = (user) => {
    setDeleteTarget(user);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.deleteUser(deleteTarget.uid);
      setSuccessMsg('User deleted successfully');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchUsers();
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to delete user');
    }
  };


  const getCountryName = (countryId) => {
    const match = countries.find((c) => String(c.c_id) === String(countryId));
    return match?.countryName || '';
  };

  const getCountryIso2 = (countryId) => {
    const match = countries.find((c) => String(c.c_id) === String(countryId));
    const raw =
      match?.iso2 ||
      match?.country_iso2 ||
      match?.countryIso2 ||
      match?.countryCode ||
      match?.country_code ||
      match?.c_code ||
      match?.code ||
      match?.alpha2 ||
      '';
    return raw ? String(raw).toUpperCase() : '';
  };

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

  const validatePassword = (value) => {
    const password = String(value || '');
    const hasLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    return hasLength && hasUpper && hasLower && hasNumber && hasSpecial;
  };

  const validatePhone = (value, countryId) => {
    const phone = String(value || '').trim();
    if (!phone) return 'Mobile number is required';
    if (!/^\d{6,15}$/.test(phone)) return 'Enter a valid phone number';
    const iso2 = getCountryIso2(countryId);
    if (iso2) {
      const parsed = parsePhoneNumberFromString(phone, iso2);
      if (!parsed || !parsed.isValid()) {
        return `Enter a valid ${getCountryName(countryId) || 'phone'} number`;
      }
    }
    return '';
  };

  const validateAddForm = () => {
    const nextErrors = {};
    const nameValue = String(username || '').trim();
    if (!nameValue || nameValue.length < 3) {
      nextErrors.username = 'Username must be at least 3 characters long';
    } else if (!/^[a-zA-Z\s]+$/.test(nameValue)) {
      nextErrors.username = 'Username should contain only letters';
    }
    if (!validateEmail(userEmail)) {
      nextErrors.userEmail = 'Enter a valid email address';
    }
    const phoneError = validatePhone(mobile, country);
    if (phoneError) {
      nextErrors.mobile = phoneError;
    }
    if (!country) {
      nextErrors.country = 'Please select a country';
    }
    if (!userRole) {
      nextErrors.userRole = 'Please select a role';
    }
    if (!validatePassword(userPassword)) {
      nextErrors.userPassword = 'Password must include upper, lower, number, and symbol';
    }
    setAddErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateEditForm = () => {
    const nextErrors = {};
    const nameValue = String(editUsername || '').trim();
    if (!nameValue || nameValue.length < 3) {
      nextErrors.editUsername = 'Username must be at least 3 characters long';
    } else if (!/^[a-zA-Z\s]+$/.test(nameValue)) {
      nextErrors.editUsername = 'Username should contain only letters';
    }
    if (editEmail && !validateEmail(editEmail)) {
      nextErrors.editEmail = 'Enter a valid email address';
    }
    if (!editCountry) {
      nextErrors.editCountry = 'Please select a country';
    }
    if (!editRole) {
      nextErrors.editRole = 'Please select a role';
    }
    setEditErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  const getProfileImage = (image) => resolveUserAvatar(image);

  const visibleUsers = users.filter((item) => {
    const roleValue = normalizeRole(item.u_role);
    return roleValue === ROLE_KEYS.AGENT || roleValue === ROLE_KEYS.AGENT_ADMIN;
  });

  const filteredData = visibleUsers.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      (item.u_name || '').toLowerCase().includes(search) ||
      (item.u_email || '').toLowerCase().includes(search) ||
      (item.u_mobile || '').toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  // Stats calculations
  const totalUsers = visibleUsers.length;
  const activeUsers = visibleUsers.filter((u) => (u.u_status ?? u.u_active) === 1).length;
  const inactiveUsers = visibleUsers.filter((u) => (u.u_status ?? u.u_active) !== 1).length;
  const agentAdmins = visibleUsers.filter((u) => normalizeRole(u.u_role) === ROLE_KEYS.AGENT_ADMIN).length;

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    if (!validateAddForm()) {
      setErrorMsg('Please fix the highlighted fields');
      return;
    }

    try {
      const payload = {
        name: username,
        email: userEmail,
        mobile,
        password: userPassword,
        country,
        role: userRole,
      };
      if (userAvatarFile) {
        const formData = new FormData();
        formData.append('profileImage', userAvatarFile);
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined && value !== null && String(value).length > 0) {
            formData.append(key, value);
          }
        });
        await usersApi.createAgent(formData);
      } else {
        await usersApi.createAgent(payload);
      }
      setSuccessMsg('User created successfully');
      setShowAddModal(false);
      setUsername('');
      setUserEmail('');
      setMobile('');
      setUserPassword('');
      setUserRole('');
      setUserAvatarFile(null);
      setUserAvatarPreview('');
      setAddErrors({});
      fetchUsers();
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Registration failed');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!validateEditForm()) {
      setErrorMsg('Please fix the highlighted fields');
      return;
    }

    try {
      const payload = {
        name: editUsername,
        email: editEmail,
        mobile: editMobile,
        country: editCountry,
        role: editRole,
      };
      if (editAvatarFile) {
        const formData = new FormData();
        formData.append('profileImage', editAvatarFile);
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined && value !== null && String(value).length > 0) {
            formData.append(key, value);
          }
        });
        await usersApi.updateAgent(editId, formData);
      } else {
        await usersApi.updateAgent(editId, payload);
      }
      setSuccessMsg('User updated successfully');
      setShowEditModal(false);
      setEditAvatarFile(null);
      setEditAvatarPreview('');
      setEditErrors({});
      fetchUsers();
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Update failed');
    }
  };

  const openEditModal = async (user) => {
    try {
      const response = await adminApi.getUserById(user.uid);
      const data = response?.data?.data || response?.data || user;
      setEditId(data.uid);
      setEditUsername(data.u_name || '');
      setEditEmail(data.u_email || '');
      setEditMobile(data.u_mobile || '');
      setEditCountry(data.u_country || '');
      setEditRole(data.u_role || '');
      setEditAvatarFile(null);
      setEditAvatarPreview(getProfileImage(data.u_image));
      setEditErrors({});
      setShowEditModal(true);
    } catch (error) {
      setErrorMsg(error?.response?.data?.error || 'Failed to load user details');
    }
  };

  const getRoleOptions = () => {
    return [
      { value: '', label: '-- Select Option --' },
      { value: 'agentAdmin', label: 'Agent Admin' },
      { value: 'agent', label: 'Agent' },
    ];
  };

  const getRoleLabel = (value) => {
    const option = getRoleOptions().find((item) => item.value === value);
    return option?.label || value;
  };


  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              User Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage agents and agent admins
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            {canAddUser && (
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={() => setShowAddModal(true)}
                sx={{ borderRadius: 2 }}
              >
                New User
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 3 }}
        flexWrap="wrap"
      >
        <StatCard
          icon={Users}
          label="Total Users"
          value={totalUsers}
          color="#6366f1"
        />
        <StatCard
          icon={UserCheck}
          label="Active"
          value={activeUsers}
          color="#22c55e"
        />
        <StatCard
          icon={UserX}
          label="Inactive"
          value={inactiveUsers}
          color="#f59e0b"
        />
        <StatCard
          icon={Shield}
          label="Agent Admins"
          value={agentAdmins}
          color="#3b82f6"
        />
      </Stack>

      {/* Alerts */}
      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      )}

      {/* Main Content Card */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {/* Toolbar */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            size="small"
            sx={{
              minWidth: 280,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#9ca3af" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, width: 60 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 140 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 160 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Loading users...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : currentData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 8 }}>
                    <Stack alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: '50%',
                          bgcolor: 'grey.100',
                        }}
                      >
                        <Users size={40} color="#9ca3af" />
                      </Box>
                      <Typography color="text.secondary">
                        {searchTerm ? 'No users match your search' : 'No users available'}
                      </Typography>
                      {!searchTerm && canAddUser && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Plus size={16} />}
                          onClick={() => setShowAddModal(true)}
                        >
                          Add Your First User
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                currentData.map((item, index) => {
                  const isDeleted = item.u_status === 0;
                  const isActive = (item.u_status ?? item.u_active) === 1;
                  return (
                    <TableRow
                      key={item.uid || index}
                      hover
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        opacity: isDeleted ? 0.6 : 1,
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {startIndex + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            src={getProfileImage(item.u_image)}
                            alt={item.u_name}
                            sx={{
                              width: 40,
                              height: 40,
                              cursor: 'pointer',
                              border: '2px solid',
                              borderColor: isActive ? 'success.main' : 'grey.300',
                            }}
                            onClick={() => {
                              setSelectedImage(getProfileImage(item.u_image));
                              setShowImageModal(true);
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {item.u_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {item.uid}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2">{item.u_email || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.u_mobile || '-'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getRoleLabel(normalizeRole(item.u_role))}
                          size="small"
                          color={normalizeRole(item.u_role) === ROLE_KEYS.AGENT_ADMIN ? 'primary' : 'default'}
                          variant="outlined"
                          sx={{ borderRadius: 1.5, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getCountryName(item.u_country) || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(item.u_created)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isDeleted ? (
                          <Chip label="Deleted" size="small" color="error" />
                        ) : (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Switch
                              size="small"
                              checked={isActive}
                              onChange={() => handleStatusChange(item.uid, item.u_status ?? item.u_active)}
                              color="success"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {isActive ? 'Active' : 'Inactive'}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!isDeleted && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <IconButton
                              size="small"
                              onClick={() => openEditModal(item)}
                              sx={{ color: 'primary.main' }}
                            >
                              <Pencil size={16} />
                            </IconButton>
                          
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteConfirm(item)}
                              sx={{ color: 'error.main' }}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                Showing {startIndex + 1} - {Math.min(startIndex + rowsPerPage, filteredData.length)} of {filteredData.length}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={20} />
                </IconButton>
                <Stack direction="row" spacing={0.5}>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="small"
                        variant={currentPage === pageNum ? 'contained' : 'text'}
                        onClick={() => setCurrentPage(pageNum)}
                        sx={{
                          minWidth: 36,
                          borderRadius: 1.5,
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={20} />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        )}
      </Card>

      {/* Add User Dialog */}
      <Dialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Add New User
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create a new agent or agent admin
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Profile Image (JPEG/PNG)</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={userAvatarPreview || '/public/assets/images/users/user.jpeg'}
                  sx={{ width: 56, height: 56 }}
                />
                <Stack spacing={0.5}>
                  <Button variant="outlined" component="label">
                    Upload
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/png"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setUserAvatarFile(file);
                        setUserAvatarPreview(file ? URL.createObjectURL(file) : '');
                      }}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Square image looks best.
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            <TextField
              label="Username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setAddErrors((prev) => ({ ...prev, username: undefined }));
              }}
              inputProps={{ minLength: 3, maxLength: 20 }}
              required
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2 } }}
              error={!!addErrors.username}
              helperText={addErrors.username}
            />
            <TextField
              label="Email"
              type="email"
              value={userEmail}
              onChange={(e) => {
                setUserEmail(e.target.value);
                setAddErrors((prev) => ({ ...prev, userEmail: undefined }));
              }}
              required
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2 } }}
              error={!!addErrors.userEmail}
              helperText={addErrors.userEmail}
            />
            <FormControl fullWidth size="small" error={!!addErrors.country}>
              <InputLabel id="country-label">Country</InputLabel>
              <Select
                labelId="country-label"
                value={country}
                label="Country"
                onChange={(e) => {
                  setCountry(e.target.value);
                  setAddErrors((prev) => ({ ...prev, country: undefined }));
                }}
                renderValue={(selected) => {
                  if (!selected) return 'Select country';
                  return getCountryName(selected);
                }}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="" disabled>
                  Select country
                </MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c.c_id} value={c.c_id}>
                    {c.countryName}
                  </MenuItem>
                ))}
              </Select>
              {addErrors.country && <FormHelperText>{addErrors.country}</FormHelperText>}
            </FormControl>
            <TextField
              label="Mobile"
              type="number"
              value={mobile}
              onChange={(e) => {
                setMobile(e.target.value);
                setAddErrors((prev) => ({ ...prev, mobile: undefined }));
              }}
              inputProps={{ minLength: 8, maxLength: 11 }}
              required
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2 } }}
              error={!!addErrors.mobile}
              helperText={addErrors.mobile}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={userPassword}
              onChange={(e) => {
                setUserPassword(e.target.value);
                setAddErrors((prev) => ({ ...prev, userPassword: undefined }));
              }}
              inputProps={{ minLength: 8, maxLength: 20 }}
              required
              fullWidth
              size="small"
              InputProps={{
                sx: { borderRadius: 2 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" size="small">
                      {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={!!addErrors.userPassword}
              helperText={addErrors.userPassword}
            />
            <FormControl fullWidth required size="small" error={!!addErrors.userRole}>
              <InputLabel id="role-label" shrink>
                Role
              </InputLabel>
              <Select
                labelId="role-label"
                value={userRole}
                label="Role"
                onChange={(e) => {
                  setUserRole(e.target.value);
                  setAddErrors((prev) => ({ ...prev, userRole: undefined }));
                }}
                renderValue={(selected) =>
                  selected ? (
                    getRoleLabel(selected)
                  ) : (
                    <Typography component="span" color="text.disabled">
                      Select role
                    </Typography>
                  )
                }
                displayEmpty
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="" disabled>
                  Select role
                </MenuItem>
                {getRoleOptions().map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {addErrors.userRole && <FormHelperText>{addErrors.userRole}</FormHelperText>}
            </FormControl>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            variant="outlined"
            onClick={() => setShowAddModal(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSubmit}
            sx={{ borderRadius: 2 }}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Edit User
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update agent or agent admin details
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <TextField
              label="Username"
              value={editUsername}
              onChange={(e) => {
                setEditUsername(e.target.value);
                setEditErrors((prev) => ({ ...prev, editUsername: undefined }));
              }}
              inputProps={{ minLength: 3, maxLength: 20 }}
              required
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2 } }}
              error={!!editErrors.editUsername}
              helperText={editErrors.editUsername}
            />
            <TextField
              label="Email"
              type="email"
              value={editEmail}
              onChange={(e) => {
                setEditEmail(e.target.value);
                setEditErrors((prev) => ({ ...prev, editEmail: undefined }));
              }}
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2 } }}
              error={!!editErrors.editEmail}
              helperText={editErrors.editEmail}
            />
            <FormControl fullWidth size="small" error={!!editErrors.editCountry}>
              <InputLabel id="edit-country-label">Country</InputLabel>
              <Select
                labelId="edit-country-label"
                value={editCountry}
                label="Country"
                onChange={(e) => {
                  setEditCountry(e.target.value);
                  setEditErrors((prev) => ({ ...prev, editCountry: undefined }));
                }}
                renderValue={(selected) => {
                  if (!selected) return 'Select country';
                  return getCountryName(selected);
                }}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="" disabled>
                  Select country
                </MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c.c_id} value={c.c_id}>
                    {c.countryName}
                  </MenuItem>
                ))}
              </Select>
              {editErrors.editCountry && <FormHelperText>{editErrors.editCountry}</FormHelperText>}
            </FormControl>
            <TextField
              label="Mobile"
              type="number"
              value={editMobile}
              fullWidth
              size="small"
              InputProps={{ sx: { borderRadius: 2 }, readOnly: true }}
            />
            <FormControl fullWidth size="small" error={!!editErrors.editRole}>
              <InputLabel id="edit-role-label" shrink>
                Role
              </InputLabel>
              <Select
                labelId="edit-role-label"
                value={editRole}
                label="Role"
                onChange={(e) => {
                  setEditRole(e.target.value);
                  setEditErrors((prev) => ({ ...prev, editRole: undefined }));
                }}
                renderValue={(selected) =>
                  selected ? (
                    getRoleLabel(selected)
                  ) : (
                    <Typography component="span" color="text.disabled">
                      Select role
                    </Typography>
                  )
                }
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="" disabled>
                  Select role
                </MenuItem>
                {getRoleOptions().map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {editErrors.editRole && <FormHelperText>{editErrors.editRole}</FormHelperText>}
            </FormControl>
            <Stack spacing={1}>
              <Typography variant="subtitle2">Profile Image (JPEG/PNG)</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={editAvatarPreview || '/public/assets/images/users/user.jpeg'}
                  sx={{ width: 56, height: 56 }}
                />
                <Button variant="outlined" component="label">
                  Upload
                  <input
                    type="file"
                    hidden
                    accept="image/jpeg,image/png"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setEditAvatarFile(file);
                      setEditAvatarPreview(file ? URL.createObjectURL(file) : editAvatarPreview);
                    }}
                  />
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            variant="outlined"
            onClick={() => setShowEditModal(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            sx={{ borderRadius: 2 }}
          >
            Update User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Profile Picture
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Box
            component="img"
            src={selectedImage}
            alt="Profile"
            sx={{
              maxWidth: '100%',
              maxHeight: 400,
              borderRadius: 2,
              boxShadow: 1,
            }}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => setShowImageModal(false)}
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete {deleteTarget?.u_name || 'this user'}?
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            variant="outlined"
            onClick={() => setShowDeleteModal(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default UsersPage;
